import { useContext, useState, useEffect, useCallback } from 'react';
import { RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import { FlowForm, FlowFormLeaveContext, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, List, ListItem } from 't-lib-ui/structure';
import { AppState } from '../../context/general';
import { getAdminMagicSecret } from '../../connector/auth';
import { TextButton, TabButtons } from 't-lib-ui/widgets';
import { chevronBackIcon, openIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import AdminHeader from '../../components/admin/AdminHeader';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import {
  adminListHolds, adminUpdateHold, adminListDeletedUsers, adminUpdateDeletedUser, adminListRetentionLog,
} from '../../connector/api';
import { RestHoldRecord, RestDeletedUser, RestRetentionLogEntry } from '../../schema-copy/auth-schema';

type Tab = 'bans' | 'holds' | 'log';

const TABS: Tab[] = ['bans', 'holds', 'log'];
const TAB_LABELS = ['Bans', 'Holds', 'Log'];

// Legal-hold retention category (RETENTION-CONCEPT §2).
const CATEGORY_LABELS: Record<number, string> = {
  1: 'CSAM preservation',
  3: 'Takedown evidence',
  4: 'Legal-claims defence',
};

const formatDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

// 'none' | 'permanent' | 'until:<ISO>' -> human label.
const banLabel = (banStatus: string): string => {
  if (banStatus == 'none') return 'Not banned';
  if (banStatus == 'permanent') return 'Permanent ban';
  if (banStatus.startsWith('until:')) return `Banned until ${formatDate(banStatus.slice(6))}`;
  return banStatus;
};

const isBanned = (t: RestDeletedUser): boolean => {
  if (t.banStatus == 'none') return false;
  if (t.banStatus.startsWith('until:')) return new Date(t.banStatus.slice(6)).getTime() > Date.now();
  return true;
};

const shortHash = (h: string): string => (h.length > 20 ? h.slice(0, 16) + '…' : h);

const creatorLabel = (h: RestHoldRecord): string => {
  const snap = h.creatorSnapshot as { nickName?: string } | undefined;
  const parts = [
    snap?.nickName ? `“${snap.nickName}”` : null,
    h.creatorAccountId ? `acct ${h.creatorAccountId}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'unresolved';
};

const AdminRetentionPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('bans');
  const [bans, setBans] = useState<RestDeletedUser[]>([]);
  const [holds, setHolds] = useState<RestHoldRecord[]>([]);
  const [log, setLog] = useState<RestRetentionLogEntry[]>([]);
  const [activeHoldsOnly, setActiveHoldsOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const magic = getAdminMagicSecret() || adminSecret || '';

  useFlowFormGlobal();

  useEffect(() => {
    registerKeyHandler('adminRetentionPage', {
      'Escape': () => { goTo(routerCtx, '/a/admin/feeds'); return true; },
    }, false, 0);
    return () => unregisterKeyHandler('adminRetentionPage');
  }, [routerCtx]);

  const load = useCallback(async () => {
    const auth = appState.state.authData;
    if (!auth || !magic) return;
    setLoading(true);
    try {
      if (tab == 'bans') setBans(await adminListDeletedUsers(auth, magic));
      else if (tab == 'holds') setHolds(await adminListHolds(auth, magic, activeHoldsOnly));
      else setLog(await adminListRetentionLog(auth, magic));
    } catch (e) {
      console.error('Failed to load retention data:', e);
    } finally {
      setLoading(false);
    }
  }, [appState.state.authData, magic, tab, activeHoldsOnly]);

  useEffect(() => { load(); }, [load]);

  const toggleBan = useCallback(async (t: RestDeletedUser) => {
    const auth = appState.state.authData;
    if (!auth || !magic) return;
    const banStatus = isBanned(t) ? 'none' : 'permanent';
    setBusyKey(t.emailHash);
    try {
      await adminUpdateDeletedUser(auth, t.emailHash, banStatus, magic,
        banStatus == 'permanent' ? 'manual admin ban' : undefined);
      await load();
    } catch (e) {
      console.error('Failed to update ban:', e);
    } finally {
      setBusyKey(null);
    }
  }, [appState.state.authData, magic, load]);

  const toggleLeHold = useCallback(async (h: RestHoldRecord) => {
    const auth = appState.state.authData;
    if (!auth || !magic) return;
    setBusyKey(h.reportId);
    try {
      await adminUpdateHold(auth, h.reportId, !h.lawEnforcementHold, magic);
      await load();
    } catch (e) {
      console.error('Failed to update hold:', e);
    } finally {
      setBusyKey(null);
    }
  }, [appState.state.authData, magic, load]);

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-retention"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
                <AdminHeader title="Retention & Legal Hold" onExpand={onExpand} />
                <p style={{ marginBottom: '16px', color: 'var(--gray-400)' }}>
                  Records kept past account deletion for a specific legal purpose (see RETENTION-CONCEPT.md).
                  <strong> Bans</strong> carry repeat-infringer identity forward; <strong>holds</strong> preserve
                  evidence frozen when a report was confirmed; the <strong>log</strong> is the accountability trail.
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <TextButton label="Back" icon={chevronBackIcon} onClick={() => goTo(routerCtx, '/a/admin/feeds')} focussable="flow" size="small" type="minor" />
                  <TabButtons
                    labels={TAB_LABELS}
                    selectedIndex={TABS.indexOf(tab)}
                    onClick={(i) => setTab(TABS[i])}
                    focusId="retention-tabs"
                  />
                  {tab == 'holds' && (
                    <div style={{ marginLeft: 'auto' }}>
                      <TextButton
                        label={activeHoldsOnly ? 'Showing: Active' : 'Showing: All'}
                        onClick={() => setActiveHoldsOnly(v => !v)}
                        focussable="flow"
                        size="small"
                        type="minor"
                      />
                    </div>
                  )}
                </div>

                {/* ---- Bans ---- */}
                {tab == 'bans' && (
                  <List>
                    {!loading && bans.length == 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                        No tombstones. No banned or strike-carrying identities.
                      </div>
                    )}
                    {bans.map((t) => (
                      <ListItem key={t.emailHash} focussable="normal">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>
                              <span style={{ color: isBanned(t) ? 'var(--error-color)' : 'var(--gray-300)' }}>
                                {banLabel(t.banStatus)}
                              </span>
                              <span style={{ color: 'var(--gray-500)', fontWeight: 400, marginLeft: '8px', fontSize: '0.85em' }}>
                                {t.strikeCount} strike{t.strikeCount == 1 ? '' : 's'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.8em', color: 'var(--gray-500)', marginTop: '4px', fontFamily: 'monospace' }} title={t.emailHash}>
                              {shortHash(t.emailHash)}
                            </div>
                            {t.banReason && (
                              <div style={{ fontSize: '0.9em', color: 'var(--gray-200)', marginTop: '6px' }}>
                                Reason: {t.banReason}
                              </div>
                            )}
                            <div style={{ fontSize: '0.8em', color: 'var(--gray-500)', marginTop: '4px' }}>
                              Deleted {formatDate(t.deletedAt)}
                              {t.lastStrikeAt ? ` · last strike ${formatDate(t.lastStrikeAt)}` : ''}
                              {t.infringementRefs.length ? ` · ${t.infringementRefs.map(r => r.reason).join(', ')}` : ''}
                            </div>
                          </div>
                          <div style={{ flexShrink: 0 }}>
                            <TextButton
                              label={isBanned(t) ? 'Lift ban' : 'Ban permanently'}
                              onClick={() => toggleBan(t)}
                              focussable="flow"
                              size="small"
                              type={isBanned(t) ? 'minor' : 'major'}
                              enabled={busyKey == t.emailHash ? 'disabled' : 'enabled'}
                            />
                          </div>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* ---- Holds ---- */}
                {tab == 'holds' && (
                  <List>
                    {!loading && holds.length == 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                        {activeHoldsOnly ? 'No active holds.' : 'No hold records.'}
                      </div>
                    )}
                    {holds.map((h) => (
                      <ListItem key={h.id} focussable="normal">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>
                              {h.category == 1 && (
                                <span style={{ color: 'var(--error-color)', marginRight: '8px', textTransform: 'uppercase', fontSize: '0.8em' }}>
                                  CSAM
                                </span>
                              )}
                              {CATEGORY_LABELS[h.category] ?? `Category ${h.category}`}
                              <span style={{ color: 'var(--gray-500)', fontWeight: 400, marginLeft: '8px', fontSize: '0.85em' }}>
                                {h.reason}{h.strike ? ' · strike' : ''}
                              </span>
                              {h.releasedAt && (
                                <span style={{ color: 'var(--gray-500)', fontWeight: 400, marginLeft: '8px', fontSize: '0.85em' }}>(released)</span>
                              )}
                              {h.lawEnforcementHold && (
                                <span style={{ color: 'var(--warning-color, var(--gray-300))', fontWeight: 400, marginLeft: '8px', fontSize: '0.85em' }}>· expiry blocked</span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.85em', color: 'var(--gray-400)', marginTop: '4px' }}>
                              Feed {h.feedId}{h.postIndex != null ? ` · post #${h.postIndex}` : ''} · creator {creatorLabel(h)}
                            </div>
                            <div style={{ fontSize: '0.8em', color: 'var(--gray-500)', marginTop: '4px' }}>
                              Frozen {formatDate(h.createdAt)} · {h.lawEnforcementHold ? `won't expire (would have on ${formatDate(h.expiresAt)})` : `expires ${formatDate(h.expiresAt)}`}
                              {h.hasPostSnapshot ? ' · reported post preserved' : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                            <TextButton
                              label="Open feed"
                              icon={openIcon}
                              onClick={() => goTo(routerCtx, `/a/admin/feed/${h.feedId}`)}
                              focussable="flow"
                              size="small"
                              type="minor"
                            />
                            <TextButton
                              label={h.lawEnforcementHold ? 'Allow expiry' : 'Prevent expiry'}
                              onClick={() => toggleLeHold(h)}
                              focussable="flow"
                              size="small"
                              type={h.lawEnforcementHold ? 'minor' : 'major'}
                              enabled={busyKey == h.reportId ? 'disabled' : 'enabled'}
                            />
                          </div>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                )}

                {/* ---- Log ---- */}
                {tab == 'log' && (
                  <List>
                    {!loading && log.length == 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                        No retention-log entries yet.
                      </div>
                    )}
                    {log.map((e) => (
                      <ListItem key={e.id} focussable="normal">
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {e.event}
                            <span style={{ color: 'var(--gray-500)', fontWeight: 400, marginLeft: '8px', fontSize: '0.85em' }}>
                              {formatDate(e.at)}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.8em', color: 'var(--gray-500)', marginTop: '4px' }}>
                            {[e.accountId ? `acct ${e.accountId}` : null, e.emailHash ? `hash ${shortHash(e.emailHash)}` : null].filter(Boolean).join(' · ') || '—'}
                          </div>
                          {e.details && (
                            <pre style={{ fontSize: '0.75em', color: 'var(--gray-400)', marginTop: '6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(e.details)}
                            </pre>
                          )}
                        </div>
                      </ListItem>
                    ))}
                  </List>
                )}
              </div>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminRetentionPage;
